import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AccountBalanceWallet, SouthWest, NorthEast, SwapHoriz } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { createFilterOptions, Util } from '../../util/Util';
import RefreshButton from '../../components/RefreshButton';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  MoleDialog,
  MoleDialogHeader,
  useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { Z_INDEX } from '../../styles/zIndex';
import type { ClientWalletRow, WalletPostPreset, WalletRecordType, WalletTagOption } from './walletTypes';
import { excludeExpensePayModes } from '../../util/constants';
import { MONEY_LIMITS, parseMoneyInput } from '../../util/quantity';
import {
  MANUAL_SYSTEM_WALLET_TAGS,
  TRANSFER_TAG,
  defaultTagForType,
  isReceiptableWalletRecord,
  isReservedWalletTag,
  pairedTypeForTag,
} from './walletTypes';
import { businessTodayDate } from "../../util/businessClock";
import { offerReceipt } from '../../util/receiptPrompt';

type PostMode = WalletRecordType | 'TRANSFER';

type Props = {
  isOpen: boolean;
  wallet: ClientWalletRow | null;
  wallets: ClientWalletRow[];
  onClose: () => void;
  onSuccess: () => void;
  onWalletsChange?: (wallets: ClientWalletRow[]) => void;
  preset?: WalletPostPreset;
};

const WalletRecordModal = ({ isOpen, wallet, wallets, onClose, onSuccess, onWalletsChange, preset }: Props) => {
  const { t } = useTranslation();
  const { api, access } = useAuth();
  const { setLoadingMessage } = useUI();
  const { appSettings } = useAppSocket();
  const themeColor = useMoleThemeColor();
  const [wcDate, setWcDate] = useState<Date>(() => businessTodayDate());
  const [target, setTarget] = useState<ClientWalletRow | null>(null);
  const [walletOptions, setWalletOptions] = useState<ClientWalletRow[]>(wallets);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [mode, setMode] = useState<PostMode>('CREDIT');
  const [amount, setAmount] = useState<number>(0);
  const [tag, setTag] = useState('advance');
  const [tagOptions, setTagOptions] = useState<WalletTagOption[]>([]);
  const [payMode, setPayMode] = useState<{ id: number; mode_name: string } | null>(null);
  const [payModes, setPayModes] = useState<{ id: number; mode_name: string }[]>([]);
  const [description, setDescription] = useState('');
  const [sourceClient, setSourceClient] = useState<number | ''>('');
  const [ref, setRef] = useState('');
  const lockClient = !!wallet;
  const lockTag = !!preset?.lockTag;
  const lockMode = !!preset?.lockMode;

  const applyWallets = (rows: ClientWalletRow[]) => {
    setWalletOptions(rows);
    onWalletsChange?.(rows);
    setTarget((current) => {
      const selected = wallet ?? current;
      if (!selected) return current;
      return rows.find((row) => row.client_id === selected.client_id) || selected;
    });
  };

  const fetchWallets = async () => {
    setLoadingWallets(true);
    const res = await api.get('/wallets');
    if (res?.status === 200) {
      applyWallets(Array.isArray(res.data) ? res.data : res.data?.items || []);
    }
    setLoadingWallets(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    setTarget(wallet);
    setWalletOptions(wallets);
    setWcDate(new Date());
    setMode(preset?.mode ?? 'CREDIT');
    setAmount(preset?.amount && preset.amount > 0 ? preset.amount : 0);
    setTag(preset?.tag ?? 'advance');
    setDescription(preset?.description ?? '');
    setRef(preset?.ref ?? '');
    setSourceClient('');
    setPayMode(null);
    if (!wallets.length) void fetchWallets();
    void api.get('/wallets/tags').then((res) => {
      if (res?.status === 200 && Array.isArray(res.data)) {
        setTagOptions(res.data);
      }
    });
    void api.get('/payment/modes/active').then((res) => {
      if (res?.status === 200 && Array.isArray(res.data)) {
        setPayModes(excludeExpensePayModes(res.data));
      }
    });
  }, [isOpen, wallet?.client_id, preset?.ref, preset?.tag]);

  const activeWallets = useMemo(
    () => walletOptions.filter((row) => row.is_active),
    [walletOptions],
  );
  const peerWallets = useMemo(
    () => activeWallets.filter((row) => row.client_id !== target?.client_id),
    [activeWallets, target?.client_id],
  );
  const transfer = mode === 'TRANSFER';
  const tagChoices = useMemo(() => {
    const history = tagOptions
      .map((option) => option.tag)
      .filter((value) => {
        const key = value.trim().toLowerCase();
        return !isReservedWalletTag(key) && key !== TRANSFER_TAG;
      });
    return [...new Set([...MANUAL_SYSTEM_WALLET_TAGS.filter((item) => item !== TRANSFER_TAG), ...history])];
  }, [tagOptions]);

  const selectMode = (next: PostMode) => {
    if (lockMode) return;
    setMode(next);
    if (next === 'TRANSFER') {
      setTag(TRANSFER_TAG);
      return;
    }
    const paired = pairedTypeForTag(tag);
    if (tag.trim().toLowerCase() === TRANSFER_TAG || (paired && paired !== next)) {
      setTag(defaultTagForType(next));
      setSourceClient('');
    }
  };

  const applyTag = (value: string) => {
    if (lockTag) return;
    setTag(value);
    const paired = pairedTypeForTag(value);
    if (paired && mode !== 'TRANSFER') {
      setMode(paired);
    }
  };

  const canSubmit =
    !!target
    && amount > 0
    && (transfer || (tag.trim().length > 0 && !isReservedWalletTag(tag)))
    && (!transfer || sourceClient !== '')
    && (transfer || Boolean(payMode?.id));

  const handleSave = async () => {
    if (!target || !canSubmit) return;
    if (!Util.checkDating(Util.formatISODate(wcDate), access, 'wallet')) {
      return;
    }
    setLoadingMessage('SAVE');
    const payload: Record<string, unknown> = {
      tag: transfer ? TRANSFER_TAG : tag.trim(),
      type: transfer ? 'DEBIT' : mode,
      date: Util.formatISODate(wcDate),
      amount,
      description: description.trim() || null,
    };
    if (transfer) {
      payload.source_client = sourceClient;
    } else {
      payload.mode = payMode?.id ?? null;
    }
    if (ref.trim()) {
      payload.ref = ref.trim();
    }
    const res = await api.post(`/wallets/${target.client_id}/records`, payload);
    setLoadingMessage(null);
    if (res?.status === 201) {
      toast.success(t('common:toast.success.walletPost'), { autoClose: 3000 });
      onSuccess();
      onClose();
      // A transfer returns [source debit, destination credit]: the receipt is for the credit.
      const ids: number[] = Array.isArray(res.data?.ids) ? res.data.ids.map(Number) : [];
      const creditId = transfer ? ids[ids.length - 1] : ids[0];
      const credited = transfer || isReceiptableWalletRecord({ type: String(payload.type), tag: String(payload.tag) });
      if (creditId && credited) {
        offerReceipt({ source: { kind: 'wallet', id: creditId }, message: t('common:receipt.afterWallet') });
      }
    }
  };

  return (
    <MoleDialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      themeColor={themeColor}
      sx={{ zIndex: Z_INDEX.nestedDialog }}
      disableEnforceFocus
    >
      <MoleDialogHeader
        title={
          target
            ? t('common:wallet.postTitle', { name: target.client })
            : t('common:wallet.postMovement')
        }
        subtitle={
          target
            ? t('common:wallet.recordsTitle', {
                name: target.client,
                balance: Util.formatNumber(target.balance),
              })
            : t('common:wallet.selectClientHint')
        }
        icon={<AccountBalanceWallet />}
        onClose={onClose}
        themeColor={themeColor}
      />
      <DialogContent>
        <Stack spacing={2.25} sx={{ mt: 0.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.25,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    {t('common:tables.date')}
                    <RefreshButton isWorking={false} onClick={() => setWcDate(new Date())} />
                  </Box>
                }
                value={wcDate}
                onChange={(next) => {
                  if (!next) return;
                  const date = next instanceof Date ? next : next.toDate();
                  if (Number.isNaN(date.getTime())) return;
                  setWcDate(date);
                }}
                slotProps={{
                  textField: { fullWidth: true, variant: "filled" },
                  popper: { sx: { zIndex: Z_INDEX.nestedPopover } },
                  dialog: { sx: { zIndex: Z_INDEX.nestedPopover } },
                }}
              />
            </LocalizationProvider>
            <Autocomplete
              options={activeWallets}
              filterOptions={createFilterOptions((row) => row.client || '')}
              getOptionLabel={(row) => row.client || ''}
              isOptionEqualToValue={(option, value) => option.client_id === value.client_id}
              value={target}
              onChange={(_, row) => {
                setTarget(row);
                setSourceClient('');
              }}
              loading={loadingWallets}
              disabled={lockClient}
              slotProps={{ popper: { sx: { zIndex: Z_INDEX.nestedPopover } } }}
              renderOption={(props, row) => (
                <li {...props} key={row.client_id}>
                  {row.client} · {Util.formatNumber(row.balance)}
                </li>
              )}
              renderInput={(params) => (
                <TextField variant="filled"
                  {...params}
                  required
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {t('common:tables.client')}
                      {!lockClient && (
                        <RefreshButton
                          isWorking={loadingWallets}
                          onClick={fetchWallets}
                          disabled={loadingWallets}
                        />
                      )}
                    </Box>
                  }
                  helperText={lockClient ? undefined : t('common:wallet.selectClientHint')}
                />
              )}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 1.25,
            }}
          >
            <SideCard
              active={mode === 'CREDIT'}
              color="#2e7d32"
              icon={<SouthWest />}
              title={t('common:wallet.credit')}
              hint={t('common:wallet.creditHint')}
              disabled={lockMode}
              onClick={() => selectMode('CREDIT')}
            />
            <SideCard
              active={mode === 'DEBIT'}
              color="#c62828"
              icon={<NorthEast />}
              title={t('common:wallet.debit')}
              hint={t('common:wallet.debitHint')}
              disabled={lockMode}
              onClick={() => selectMode('DEBIT')}
            />
            <SideCard
              active={mode === 'TRANSFER'}
              color="#1565c0"
              icon={<SwapHoriz />}
              title={t('common:wallet.tags.transfer')}
              hint={t('common:wallet.transferCardHint')}
              disabled={lockMode}
              onClick={() => selectMode('TRANSFER')}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: transfer ? '1fr' : '1fr 1fr 1fr' },
              gap: 1.25,
              alignItems: 'start',
            }}
          >
            <TextField variant="filled"
              type="number"
              label={t('common:tables.amount')}
              value={Number.isFinite(amount) ? amount : 0}
              onFocus={(e) => e.target.select()}
              inputProps={{ min: 0.01, max: MONEY_LIMITS.max, step: 0.01 }}
              onChange={(e) => {
                const parsed = parseMoneyInput(e.target.value);
                if (parsed === null) return;
                setAmount(Math.max(0, Math.min(MONEY_LIMITS.max, parsed)));
              }}
            />
            {!transfer && (
              <Autocomplete
                options={payModes}
                getOptionLabel={(row) => row.mode_name || ''}
                isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                value={payMode}
                onChange={(_, row) => setPayMode(row)}
                slotProps={{ popper: { sx: { zIndex: Z_INDEX.nestedPopover } } }}
                renderInput={(params) => (
                  <TextField
                    variant="filled"
                    {...params}
                    required
                    label={t('common:tables.mode')}
                    helperText={t('common:wallet.modeHint')}
                  />
                )}
              />
            )}
            {!transfer && (
              <Autocomplete
                freeSolo
                options={tagChoices}
                value={tag}
                onInputChange={(_, value) => applyTag(value)}
                disabled={lockTag}
                slotProps={{ popper: { sx: { zIndex: Z_INDEX.nestedPopover } } }}
                renderInput={(params) => (
                  <TextField variant="filled"
                    {...params}
                    label={t('common:wallet.tag')}
                    helperText={t('common:wallet.tagHint')}
                  />
                )}
              />
            )}
          </Box>

          {transfer && (
            <Autocomplete
              options={peerWallets}
              filterOptions={createFilterOptions((row) => row.client || '')}
              getOptionLabel={(row) => row.client || ''}
              isOptionEqualToValue={(option, value) => option.client_id === value.client_id}
              value={peerWallets.find((row) => row.client_id === sourceClient) || null}
              onChange={(_, row) => setSourceClient(row?.client_id ?? '')}
              loading={loadingWallets}
              slotProps={{ popper: { sx: { zIndex: Z_INDEX.nestedPopover } } }}
              renderOption={(props, row) => (
                <li {...props} key={row.client_id}>
                  {row.client} · {Util.formatNumber(row.balance)}
                </li>
              )}
              renderInput={(params) => (
                <TextField variant="filled"
                  {...params}
                  required
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {t('common:wallet.sourceWallet')}
                      <RefreshButton
                        isWorking={loadingWallets}
                        onClick={fetchWallets}
                        disabled={loadingWallets}
                      />
                    </Box>
                  }
                  helperText={t('common:wallet.transferHint')}
                />
              )}
            />
          )}

          {!!ref && (
            <TextField variant="filled"
              label={t('common:wallet.ref')}
              value={ref}
              disabled
            />
          )}

          <TextField variant="filled"
            label={t('common:wallet.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common:ui.buttons.cancel')}</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={handleSave}>
          {t('common:ui.buttons.confirm')}
        </Button>
      </DialogActions>
    </MoleDialog>
  );
};

const SideCard = ({
  active,
  color,
  icon,
  title,
  hint,
  disabled,
  onClick,
}: {
  active: boolean;
  color: string;
  icon: ReactNode;
  title: string;
  hint: string;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      textAlign: 'left',
      border: '1px solid',
      borderColor: active ? color : 'divider',
      bgcolor: active ? `${color}14` : 'background.paper',
      color: active ? color : 'text.primary',
      borderRadius: 2.5,
      p: 1.5,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled && !active ? 0.55 : 1,
      boxShadow: active ? `0 0 0 3px ${color}22` : 'none',
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
      {icon}
      <Typography variant="subtitle1" fontWeight={800}>
        {title}
      </Typography>
    </Stack>
    <Typography variant="caption" color="text.secondary">
      {hint}
    </Typography>
  </Box>
);

export default WalletRecordModal;
